import { Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Modal } from '@shared/components/ui/Modal';

type DataTableDeleteConfirmModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Plural entity name, e.g. "candidates" or "email threads". */
  entityLabel: string;
  count: number;
  /** Optional extra line under the main message. */
  bodyExtra?: string;
  /** Checkbox label (default: acknowledgement that delete is permanent). */
  acknowledgeLabel?: string;
  onConfirm: () => void;
  /**
   * When set, renders a typed-confirm input below the checkbox.
   * The delete button stays disabled until the user types this exact string.
   * Use "DELETE" for bulk / unnamed actions.
   */
  requireTypedConfirm?: string;
};

/**
 * Delete confirmation with danger styling, trash icon, required checkbox,
 * and an optional typed-name guard for irreversible bulk deletes.
 */
export function DataTableDeleteConfirmModal({
  open,
  onClose,
  title = 'Delete selected',
  entityLabel,
  count,
  bodyExtra,
  acknowledgeLabel = 'I understand this cannot be undone',
  onConfirm,
  requireTypedConfirm,
}: DataTableDeleteConfirmModalProps) {
  const [ack, setAck] = useState(false);
  const [typedValue, setTypedValue] = useState('');

  useEffect(() => {
    if (open) {
      setAck(false);
      setTypedValue('');
    }
  }, [open]);

  const noun = count === 1 ? entityLabel.replace(/s$/i, '') : entityLabel;

  const typedMatch = requireTypedConfirm
    ? typedValue === requireTypedConfirm
    : true;

  const canConfirm = ack && typedMatch;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      variant="danger"
      icon={<Trash2 className="w-4 h-4 text-white" aria-hidden />}
      size="sm"
    >
      <p className="ggj-text-body vscode-text-secondary leading-relaxed">
        You are about to permanently delete{' '}
        <strong className="vscode-text-primary tabular-nums">{count}</strong>{' '}
        {noun}.
        {bodyExtra ? (
          <>
            {' '}
            {bodyExtra}
          </>
        ) : null}
      </p>
      <label className="mt-4 flex items-start gap-2.5 cursor-pointer select-none">
        <input
          type="checkbox"
          className="mt-0.5 rounded border-gray-500"
          checked={ack}
          onChange={(e) => setAck(e.target.checked)}
        />
        <span className="ggj-text-body vscode-text-secondary">{acknowledgeLabel}</span>
      </label>

      {requireTypedConfirm && (
        <div className="mt-3">
          <p className="ggj-text-meta vscode-text-secondary mb-1.5">
            Type <strong className="font-mono vscode-text-primary">{requireTypedConfirm}</strong> to confirm
          </p>
          <input
            type="text"
            autoComplete="off"
            spellCheck={false}
            className="w-full rounded-[10px] border vscode-border px-3 py-1.5 ggj-text-body bg-transparent vscode-text-primary focus:outline-none focus:ring-2 focus:ring-red-500/40"
            placeholder={requireTypedConfirm}
            value={typedValue}
            onChange={(e) => setTypedValue(e.target.value)}
          />
        </div>
      )}

      <div className="mt-6 flex justify-end gap-2">
        <button type="button" className="btn-secondary ggj-text-body px-3 py-1.5" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="bg-red-600 hover:bg-red-700 text-white rounded-[10px] border border-red-700 ggj-text-body px-3 py-1.5 font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          disabled={!canConfirm}
          onClick={() => {
            onConfirm();
            onClose();
          }}
        >
          Delete
        </button>
      </div>
    </Modal>
  );
}
